/** アプリごとのサイドナビの定義。旧 Jinja 画面のメニューに対応させてある。 */
import type { ComponentType } from "react";

import type { Me } from "../lib/api";
import { hasFeature } from "../lib/features";
import { hasRole } from "../lib/roles";

export type NavItem = {
  path: string;
  label: string;
  /** どの段階で実装するか(プレースホルダに出す) */
  phase: string;
  /** 実装済みの画面。無ければプレースホルダを出す */
  component?: ComponentType;
  /**
   * この項目を出すのに要るロール(例 `"idp:operator"`)。無ければアプリに
   * 入れる人全員に出す。**ここは見た目の出し分けだけ** — 実際の許可は API 側
   * (`OperatorUser` など)が持つ。持っていない人に「あるのに押せない」項目を
   * 見せないため(fix1 の低 2)。
   */
  role?: string;
  /**
   * この項目を出すのに要る機能キー(`server/app/core/features.py` の表と同じ文字列)。
   * **必須** — 新しい画面を足したときに「どの機能か」を必ず1度考えさせるため
   * (付け忘れは tsc で落ちる。綴り間違いは server の不変条件テストで落ちる)。
   * 配列なら「どれか1つでも有効なら出す」(例: デバイス台帳は Apple / Windows
   * のどちらかがあれば見せる)。**ここは見た目の出し分けだけ** — 実際の関門は
   * API 側(`require_feature`)が持つ。
   */
  feature: string | string[];
};

export type NavGroup = { label: string; items: NavItem[] };

export type AppDef = {
  key: string;
  name: string;
  groups: NavGroup[];
  /** `/<key>` に来たときに開くページ(空ならプレースホルダ1枚) */
  defaultPath?: string;
  /** ナビには出さない画面(詳細ページなど) */
  extraRoutes?: { path: string; component: ComponentType }[];
};

/**
 * いまの人に出してよい項目だけに絞った群。ロール(`NavItem.role`)と契約
 * (`NavItem.feature`)の両方で絞り、空になった群は落とす(1 項目だけの群も
 * 定義側で作らない約束だが、絞った結果で 1 つになるのは許す)。
 * `me` がまだ来ていない間は何も出さない — 後から増える方が安全。
 */
export function visibleGroups(def: AppDef, me: Me | undefined): NavGroup[] {
  return def.groups
    .map((g) => ({
      label: g.label,
      items: g.items
        .filter((it) => !it.role || hasRole(me, it.role))
        .filter((it) => hasFeature(me, it.feature)),
    }))
    .filter((g) => g.items.length > 0);
}

export function visibleItems(def: AppDef, me: Me | undefined): NavItem[] {
  return visibleGroups(def, me).flatMap((g) => g.items);
}
